import type { OrderLine } from '../types'
import { combineServing } from './serving'
import { sameLine, withQty } from './lineMath'

export interface MergedLines {
  lines: OrderLine[]
  itemCount: number
  totalKurus: number
}

/**
 * Appends one set of order lines onto another, combining anything identical.
 *
 * Two rounds of the same item collapse into one line with the quantity summed,
 * so a bill of "2 çay" plus "2 çay" reads as 4 rather than listing tea twice.
 * The note is part of what makes a line identical, soğansız and plain are
 * different things at the stall, so they never merge.
 *
 * Totals are recomputed from the unit price rather than added up, so a line
 * whose stored total drifted for any reason is corrected rather than carried
 * forward.
 */
/**
 * What was added to a ticket since it was loaded, the new round on its own.
 *
 * A ticket that already went to the stalls only needs its additions printed;
 * reprinting the whole thing would send the kitchen food it has already cooked.
 * Quantities are subtracted rather than compared, so adding a third tea to two
 * existing ones yields a line for one tea, not three.
 *
 * Lines removed at the till produce nothing here. A slip cannot un-cook
 * something, so a reduction is a matter for the person at the counter.
 */
export function newLinesSince(before: OrderLine[], after: OrderLine[]): MergedLines {
  const lines: OrderLine[] = []

  for (const line of after) {
    // Same identity rule as mergeLines: product, note, unit price, and who
    // rang it up. Without the name, one waiter's round is measured against
    // another's: A adding a second çay to a table where B already has two
    // comes out as 1 - 2 = -1, no growth, and the save button stays dead.
    const was = before.find((l) => sameLine(l, line))
    const qty = line.qty - (was?.qty ?? 0)
    if (qty > 0) {
      lines.push(withQty(line, qty, 'end'))
    }
  }

  return {
    lines,
    itemCount: lines.reduce((sum, l) => sum + l.qty, 0),
    totalKurus: lines.reduce((sum, l) => sum + l.lineTotalKurus, 0),
  }
}

/**
 * What is left on a ticket after some of its lines are taken off to be paid
 * for separately, split payment's core arithmetic.
 *
 * Same identity rule as mergeLines/newLinesSince: product, note, unit price,
 * and who rang it up. A line whose taken quantity reaches the line's own
 * quantity disappears entirely rather than lingering at zero; a taken
 * quantity larger than what remains (should not happen, but a stale
 * middle-panel selection racing a waiter's edit is not impossible) is
 * clamped at zero instead of going negative.
 */
export function subtractLines(base: OrderLine[], taken: OrderLine[]): MergedLines {
  const lines: OrderLine[] = []

  for (const line of base) {
    const removed = taken.find((l) => sameLine(l, line))
    const qty = Math.max(0, line.qty - (removed?.qty ?? 0))
    if (qty > 0) {
      // Tartılı satırda ödenen tabaklar baştan alınıyor; kalanlar sondaki.
      const next = withQty(line, qty, 'end')
      // Paid units leave; what was served cannot exceed what remains.
      if (next.servedQty !== undefined) next.servedQty = Math.min(next.servedQty, qty)
      lines.push(next)
    }
  }

  return {
    lines,
    itemCount: lines.reduce((sum, l) => sum + l.qty, 0),
    totalKurus: lines.reduce((sum, l) => sum + l.lineTotalKurus, 0),
  }
}

export function mergeLines(base: OrderLine[], incoming: OrderLine[]): MergedLines {
  const lines = [...base]

  for (const line of incoming) {
    // Price is part of what makes two lines the same, not just product and
    // note: an item repriced at the till for weight would otherwise absorb a
    // later round at the menu price, silently charging both at the new figure.
    // Two people's rounds of the same item stay apart (byName), and a
    // weighed line is its own weighing (weigh.id), see lineMath.sameLine.
    const at = lines.findIndex((l) => sameLine(l, line))
    if (at === -1) {
      lines.push(line)
    } else if (line.weigh) {
      // Aynı tartım: toplanmıyor, yeni hali eskisinin yerine (tartılmış).
      lines[at] = line
    } else {
      const qty = lines[at].qty + line.qty
      lines[at] = {
        ...lines[at],
        qty,
        lineTotalKurus: lines[at].unitPriceKurus * qty,
        // Sipariş Takip: served units add up, the older wait wins.
        ...combineServing(lines[at], line),
      }
    }
  }

  return {
    lines,
    itemCount: lines.reduce((sum, l) => sum + l.qty, 0),
    totalKurus: lines.reduce((sum, l) => sum + l.lineTotalKurus, 0),
  }
}
